// [목적] Toast/Snackbar 큐 기반 전역 알림 시스템 — ToastProvider + useToast 훅
// [이유] FEED-07 AC-11 — Follow 토글 성공/실패, 데이터 로드 오류 등 컴포넌트 트리 어디서나
//         useToast().show() 단일 API 로 알림 표시. Snackbar 큐 방식으로 동시 다발 알림 순서 보장
// [사이드 이팩트] ToastProvider 는 App 루트에 마운트 필요. Context 미주입 시 useToast() throw.
//                 MUI Snackbar + Alert 래핑 — MUI 테마 팔레트(success/error/warning/info) 사용
// [수정 시 고려사항] maxQueue=3 초과 시 오래된 알림 자동 제거. autoHideDuration=3500ms 기본값.
//                   접근성: role="status"(success/info) / role="alert"(error/warning) aria-live 자동 분기

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';

// ─── 타입 ──────────────────────────────────────────────────────────────────

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: string;
  message: string;
  severity: ToastSeverity;
  duration: number;
  /** 닫기 시작됨(퇴장 애니메이션 중, 큐에서 제거되기 전) — 중복 판정에서 제외한다(QA-P2-01) */
  closing?: boolean;
}

interface ToastContextValue {
  /**
   * [목적] 알림 표시 메서드
   * [이유] 컴포넌트가 Context 구조를 알 필요 없이 단일 API 로 Toast 표시
   * [사이드 이팩트] 큐에 항목 추가 → Snackbar 자동 표시
   * [수정 시 고려사항] duration 미전달 시 기본 3500ms 사용
   */
  show: (message: string, severity?: ToastSeverity, duration?: number) => void;
}

// ─── Context ────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_QUEUE = 3;
const DEFAULT_DURATION = 3500;

// ─── Provider ────────────────────────────────────────────────────────────────

/**
 * [목적] Toast 큐 상태 관리 + Snackbar 렌더링 Provider
 * [이유] 전역 알림을 단일 Provider 에서 관리하여 중복 Snackbar 방지
 * [사이드 이팩트] Snackbar 는 DOM 최상위에 렌더 — z-index 는 MUI 기본값(1400) 사용
 * [수정 시 고려사항] 동시 표시 Snackbar 는 1개 — 큐에서 순차 처리
 */
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [queue, setQueue] = useState<ToastItem[]>([]);
  // 현재 표시 중인 toast (큐의 첫 번째)
  const current = queue[0] ?? null;
  const [open, setOpen] = useState(false);

  /**
   * [목적] 새 Toast 항목을 큐에 추가
   * [이유] 동시 다발 알림을 순서대로 처리하기 위한 큐 패턴
   * [사이드 이팩트] MAX_QUEUE 초과 시 가장 오래된 항목 제거
   * [수정 시 고려사항] severity 기본값 'info'
   *                   [QA-P2-01] 같은 message+severity 가 이미 큐(표시 중 포함)에 있으면 추가하지 않는다 —
   *                   실패 버튼을 연타하면 똑같은 토스트가 최대 3개까지 순차로 떠서 중복으로 보였다.
   *                   단, 이미 닫히기 시작한(closing) 항목은 중복으로 보지 않는다: handleClose 는 화면에서는
   *                   즉시 닫지만 큐에서는 250ms 뒤에 빼기 때문에, 그 사이(퇴장 애니메이션이 끝나 화면에서는
   *                   사라진 뒤 ~55ms)에 온 같은 메시지를 중복으로 삼키면 영영 뜨지 않는다(CI 에서만 관측된 경합).
   *                   표시가 끝나 큐에서 빠진 뒤의 재발생도 정상적으로 다시 뜬다.
   */
  const show = useCallback(
    (message: string, severity: ToastSeverity = 'info', duration = DEFAULT_DURATION): void => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setQueue((prev) => {
        if (prev.some((t) => !t.closing && t.message === message && t.severity === severity)) {
          return prev;
        }
        const next = [...prev, { id, message, severity, duration }];
        return next.length > MAX_QUEUE ? next.slice(next.length - MAX_QUEUE) : next;
      });
      setOpen(true);
    },
    [],
  );

  /**
   * [목적] 현재 Snackbar 닫기 → 큐에서 제거 → 다음 항목 표시
   * [이유] reason==='clickaway' 시 닫기 무시 (MUI 기본 동작 준수)
   * [사이드 이팩트] 큐 shift → 다음 항목 auto-open
   * [수정 시 고려사항] 큐 잔여 항목이 있으면 close 즉시 다음 open
   */
  const handleClose = useCallback((_: React.SyntheticEvent | Event, reason?: string): void => {
    if (reason === 'clickaway') return;
    setOpen(false);
    // 닫기 시작을 표시 — show() 의 중복 판정이 닫히는 중인 항목을 무시하도록(아래 250ms 뒤 제거 전까지의 경합 방지)
    setQueue((prev) =>
      prev.length > 0 && !prev[0]!.closing
        ? [{ ...prev[0]!, closing: true }, ...prev.slice(1)]
        : prev,
    );
    // 큐 shift — 다음 항목이 있으면 250ms 후 재open (애니메이션 겹침 방지)
    setTimeout(() => {
      setQueue((prev) => {
        const next = prev.slice(1);
        if (next.length > 0) setOpen(true);
        return next;
      });
    }, 250);
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {current && (
        <Snackbar
          open={open}
          autoHideDuration={current.duration}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={(e) => handleClose(e)}
            severity={current.severity}
            variant="filled"
            role={
              current.severity === 'error' || current.severity === 'warning' ? 'alert' : 'status'
            }
            aria-live={
              current.severity === 'error' || current.severity === 'warning'
                ? 'assertive'
                : 'polite'
            }
            sx={{
              minWidth: 280,
              maxWidth: 480,
              fontSize: 13,
              fontWeight: 500,
              borderRadius: `${tokens.radius.m}px`,
              boxShadow: tokens.shadow.modal,
            }}
          >
            {current.message}
          </Alert>
        </Snackbar>
      )}
    </ToastContext.Provider>
  );
}

// ─── 훅 ─────────────────────────────────────────────────────────────────────

/**
 * [목적] useToast — ToastContext 소비 훅
 * [이유] 컴포넌트가 ToastContext 를 직접 import 하지 않고 단일 훅으로 접근
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] ToastProvider 외부에서 호출 시 throw
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within <ToastProvider>');
  }
  return ctx;
}
